// Settlement (SM-797, SM-621 phase 09a): the broker's own settlement
// position — do we pay you, or do you pay us, and how much — so a broker
// about to be billed has already seen the number. READ-ONLY: no control
// here records a settlement, moves money or adjusts a balance; recording
// an out-of-band settlement is an exchange-side action.
//
// Figures are broker-service's one engine (the exchange's backoffice reads
// the same), over a recent window (default the last 7 days), in the
// selected currency — never a total across currencies. The node is the
// session's selected node; a sibling's is never reachable (the server
// answers 404), and drill-down moves the selection to a permitted branch.
//
// SM-878 / SM-889: nothing nets or moves money. The net position and what
// collateral leaves uncovered are INFORMATION ONLY, never a demand to
// transfer; the server's outstanding_caption says so. What the exchange
// asks a broker to pay is the external credit owed, shown apart from the
// net, and each ask is a payment request listed here (this node's, and its
// branches' with the subtree switch). Every figure is the server's; a
// figure an older server does not send is not drawn.
//
//   GET /api/broker/{id}/settlement-position?currency=&from_unix_ms=&to_unix_ms=&scope=
//   GET /api/broker/{id}/settlement-notes?currency=&scope=&limit=&offset=
//   GET /api/broker/{id}/payment-requests?scope=&status=&limit=&offset=
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { capabilityGuard, Guarded } from "@/components/layout/guard";
import { PageHeader } from "@/components/layout/PageHeader";
import { useScope } from "@/components/layout/ScopeContext";
import { SectionCard } from "@/components/ui/Card";
import { Badge, type Tone } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Money } from "@/components/ui/Money";
import { Id, KeyValues, Pager, Stat, TableFrame, Time } from "@/components/ui/Common";
import { EmptyState, QueryBlock } from "@/components/ui/States";
import { Segmented } from "@/components/ui/Field";
import { fetchPaymentRequests, fetchSettlementNotes, fetchSettlementPosition, keys } from "@/lib/queries";
import {
  SETTLEMENT_DEFAULT_DAYS,
  SETTLEMENT_SPLIT_LINES,
  SETTLEMENT_WINDOWS,
  afterCollateral,
  externalCreditHintKey,
  isOverdue,
  isSettlementWindow,
  keepsSettlementPlaceholder,
  magnitude,
  mayRefetchSettlementOnFocus,
  paymentRequestStatusLabel,
  paymentRequestStatusTone,
  renewSettlementWindowIfOld,
  requestOutstanding,
  splitRequestCaptions,
  takeSettlementWindow,
  usersOpenPositionDirection,
  type SettlementWindowDays,
} from "@/lib/settlement";
import type {
  PaymentRequestItem,
  SettlementNoteItem,
  SettlementPositionDirection,
  SettlementPositionFigures,
  SettlementPositionResponse,
} from "@/lib/types";
import { useI18n } from "@/i18n/I18nProvider";
import { labelOf, serverCaption } from "@/i18n/labels";

interface SettlementSearch {
  days?: SettlementWindowDays;
  scope?: "node" | "subtree";
}

export const Route = createFileRoute("/_app/settlement")({
  validateSearch: (s: Record<string, unknown>): SettlementSearch => ({
    days: isSettlementWindow(s.days) ? (Number(s.days) as SettlementWindowDays) : undefined,
    scope: s.scope === "node" ? "node" : undefined,
  }),
  beforeLoad: capabilityGuard("FINANCE_VIEW"),
  component: SettlementPage,
});

/**
 * The page's settlement window (SM-943), held so a re-render never reads a
 * new period: a new `to` when `days` or `reads` changes (taken during
 * render, so no read goes out at the old window first), and when the tab is
 * shown again with the window over a minute old. The focus refetch reads
 * only a young window again; an old one is renewed under a new key instead,
 * whichever of the two listeners runs first. A tab left visible is renewed
 * by hand (SM-957): `refresh` takes a new window now; the page shows the
 * window's `to` as "As of" beside its Refresh button.
 */
function useSettlementWindow(days: number, reads: string) {
  const [held, setHeld] = useState(() => takeSettlementWindow(days, reads));
  let current = held;
  if (held.days !== days || held.reads !== reads) {
    current = takeSettlementWindow(days, reads);
    setHeld(current);
  }
  const latest = useRef(current);
  latest.current = current;
  useEffect(() => {
    const onShown = () => {
      if (document.visibilityState !== "visible") return;
      const next = renewSettlementWindowIfOld(latest.current);
      if (next === latest.current) return;
      latest.current = next;
      setHeld(next);
    };
    document.addEventListener("visibilitychange", onShown);
    window.addEventListener("focus", onShown);
    return () => {
      document.removeEventListener("visibilitychange", onShown);
      window.removeEventListener("focus", onShown);
    };
  }, []);
  const mayRefetchOnFocus = useCallback((query: { queryKey: readonly unknown[] }) => mayRefetchSettlementOnFocus(query.queryKey, latest.current), []);
  const refresh = useCallback(() => {
    const next = takeSettlementWindow(latest.current.days, latest.current.reads);
    latest.current = next;
    setHeld(next);
  }, []);
  return { win: current.win, mayRefetchOnFocus, refresh };
}

function dirTone(d: SettlementPositionDirection | undefined): Tone {
  return d === "BROKER_OWES_US" ? "warn" : d === "WE_OWE_BROKER" ? "accent" : "neutral";
}

function SettlementPage() {
  const { t } = useI18n();
  const { guard } = Route.useRouteContext();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { nodeId, currency } = useScope();
  const days = search.days ?? SETTLEMENT_DEFAULT_DAYS;
  const scope = search.scope ?? "subtree";
  const qc = useQueryClient();
  const settleWindow = useSettlementWindow(days, `${nodeId}|${currency}|${scope}`);
  const params = { currency, ...settleWindow.win, scope };
  const probe = { queryKey: keys.settlementPosition(nodeId, params), queryFn: () => fetchSettlementPosition(nodeId, params) };
  const q = useQuery({
    ...probe,
    enabled: guard.kind === "allow",
    refetchOnWindowFocus: settleWindow.mayRefetchOnFocus,
    // a Period, scope or renewed window keeps the figures while loading; another node or currency never shows them
    placeholderData: (prev, prevQuery) => (keepsSettlementPlaceholder(prevQuery?.queryKey, nodeId, currency) ? prev : undefined),
  });
  const set = (patch: Partial<SettlementSearch>) => void navigate({ search: (prev) => ({ ...prev, ...patch }) });

  return (
    <Guarded guard={guard} probe={probe}>
      <PageHeader title={t("settlement.title")} subtitle={t("settlement.subtitle")} coverage={scope} money />
      <div className="mb-4 flex flex-wrap items-end gap-4">
        <Segmented<string>
          label={t("settlement.period")}
          value={String(days)}
          options={SETTLEMENT_WINDOWS.map((d) => ({ value: String(d), label: t(`settlement.window.${d}`) }))}
          onChange={(v) => set({ days: Number(v) === SETTLEMENT_DEFAULT_DAYS ? undefined : (Number(v) as SettlementWindowDays) })}
        />
        <Segmented<"node" | "subtree">
          label={t("settlement.scope")}
          value={scope}
          options={[
            { value: "subtree", label: t("settlement.scope.subtree") },
            { value: "node", label: t("settlement.scope.node") },
          ]}
          onChange={(v) => set({ scope: v === "subtree" ? undefined : v })}
        />
        <div className="ml-auto flex items-center gap-2 text-xs text-t-3">
          <span>
            {t("common.asOf")} <Time ms={settleWindow.win.to_unix_ms} />
          </span>
          <Button
            variant="ghost"
            size="sm"
            disabled={q.isFetching}
            onClick={() => {
              // a new window (a new key when the minute moved on), and every
              // settlement read on the node again: requests and notes are live
              settleWindow.refresh();
              void qc.invalidateQueries({ queryKey: ["broker", nodeId] });
            }}
          >
            {t("settlement.refresh")}
          </Button>
        </div>
      </div>
      <QueryBlock query={q} lines={8}>
        {(p) => <Position p={p} scope={scope} />}
      </QueryBlock>
    </Guarded>
  );
}

function Position({ p, scope }: { p: SettlementPositionResponse; scope: "node" | "subtree" }) {
  const { t, formatNumber } = useI18n();
  const f = scope === "subtree" && p.consolidated ? p.consolidated : p.own;
  const c = p.currency;
  if (!f.has_activity) {
    return (
      <div className="space-y-4">
        <EmptyState title={t("settlement.empty")} hint={t("settlement.emptyHint")} />
        <PaymentRequests key={`${p.broker_id}:${scope}`} brokerId={p.broker_id} scope={scope} />
        <Notes brokerId={p.broker_id} currency={c} scope={scope} caption={p.note_caption} />
      </div>
    );
  }
  // information only (SM-878): never worded or styled as a demand
  const cover = afterCollateral(f);
  const uncovered = !cover.covered;
  const external = f.external_credit_outstanding !== undefined;
  const requestsOpen = f.payment_requests_open;
  const creditHint = externalCreditHintKey(f, scope);
  const receivable = p.open_receivable;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <div className={external ? "card p-5" : "card p-5 lg:col-span-2"} role="status">
          <div className="text-2xs font-semibold uppercase tracking-wide text-t-3">{t("settlement.outstanding")}</div>
          <div className="mt-2 flex flex-wrap items-baseline gap-3">
            <Badge tone={dirTone(f.direction)}>{labelOf(t, "settlement.dir", f.direction)}</Badge>
            {f.direction !== "SETTLED" && <Money currency={c} value={magnitude(f.outstanding)} className="text-2xl font-semibold" />}
          </div>
          {f.pending_note_count > 0 && (
            <div className="mt-2 flex flex-wrap items-baseline gap-2 text-xs text-t-2">
              <span>{t("settlement.ifPendingClear")}</span>
              <Badge tone={dirTone(f.if_pending_clear_direction)}>{labelOf(t, "settlement.dir", f.if_pending_clear_direction)}</Badge>
              <Money currency={c} value={magnitude(f.outstanding_if_pending_clear)} />
            </div>
          )}
          {p.outstanding_caption && <p className="mt-3 text-2xs italic text-t-3">{serverCaption(t, p.outstanding_caption)}</p>}
        </div>
        {external && (
          <div className="card p-5" data-testid="external-credit">
            <div className="text-2xs font-semibold uppercase tracking-wide text-t-3">{t("settlement.externalCredit")}</div>
            <div className="mt-2 flex flex-wrap items-baseline gap-3">
              <Badge tone={dirTone(f.external_credit_direction)}>{labelOf(t, "settlement.dir", f.external_credit_direction)}</Badge>
              {f.external_credit_direction !== "SETTLED" && (
                <Money currency={c} value={magnitude(f.external_credit_outstanding)} className="text-2xl font-semibold" />
              )}
            </div>
            {creditHint && <p className="mt-3 text-2xs text-t-3">{t(creditHint)}</p>}
          </div>
        )}
        <div className="banner" role="status">
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-t-1">{uncovered ? t("settlement.transferIn") : t("settlement.covered")}</p>
            {/* covered: the "After collateral" figure, labelled — the collateral itself has its own stat (SM-912) */}
            {!uncovered && <p className="mt-1 text-xs text-t-2">{t("settlement.uncovered")}</p>}
            <div className="mt-1 text-lg font-semibold">
              <Money currency={c} value={cover.amount} />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label={t("settlement.gross")} sub={labelOf(t, "settlement.dir", f.gross_direction)}>
          <Money currency={c} value={magnitude(f.gross)} />
        </Stat>
        <Stat label={t("settlement.periodNet")} sub={labelOf(t, "settlement.dir", f.period_direction)}>
          <Money currency={c} value={magnitude(f.period_net)} />
        </Stat>
        <Stat label={t("settlement.collateral")} sub={t("settlement.collateralNote")}>
          <Money currency={c} value={f.collateral} />
        </Stat>
        <Stat label={t("settlement.uncovered")} sub={uncovered ? t("settlement.transferIn") : t("settlement.covered")}>
          <Money currency={c} value={cover.amount} />
        </Stat>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <SectionCard title={t("settlement.periodTitle")}>
          <KeyValues
            rows={[
              { label: t("settlement.revenueReleased"), value: <Money currency={c} value={f.revenue_released} /> },
              { label: t("settlement.revenuePaid"), value: <Money currency={c} value={f.revenue_paid} /> },
              {
                label: t("settlement.clearingMovement"),
                value: f.clearing_account ? (
                  <Directed currency={c} canon={f.clearing_delta} dir={f.clearing_delta_direction} />
                ) : (
                  <span className="text-t-3">{t("settlement.noClearing")}</span>
                ),
              },
              { label: t("settlement.periodNet"), value: <Directed currency={c} canon={f.period_net} dir={f.period_direction} /> },
            ]}
          />
        </SectionCard>
        <SectionCard title={t("settlement.sinceLaunch")}>
          <KeyValues
            rows={[
              { label: t("settlement.revenueAvailable"), value: <Money currency={c} value={f.revenue_available} /> },
              ...(f.revenue_in_payout !== undefined
                ? [{ label: t("settlement.revenueInPayout"), value: <Money currency={c} value={f.revenue_in_payout} /> }]
                : []),
              {
                label: t("settlement.clearingBalance"),
                value: f.clearing_account ? (
                  <Directed currency={c} canon={f.clearing_balance} dir={f.clearing_balance_direction} />
                ) : (
                  <span className="text-t-3">{t("settlement.noClearing")}</span>
                ),
              },
              // parts of the clearing balance (SM-885, when the server sends them), never added to it
              ...(SETTLEMENT_SPLIT_LINES.openPositions && f.users_open_position_cost !== undefined
                ? [
                    {
                      label: `↳ ${t("settlement.usersOpenCost")}`,
                      value: <Directed currency={c} canon={f.users_open_position_cost} dir={usersOpenPositionDirection(f)} />,
                      hint: t("settlement.clearingPart"),
                    },
                  ]
                : []),
              ...(SETTLEMENT_SPLIT_LINES.settledResults && f.users_settled_result !== undefined
                ? [
                    {
                      label: `↳ ${t("settlement.usersSettled")}`,
                      value: <Directed currency={c} canon={f.users_settled_result} dir={f.users_settled_result_direction ?? "SETTLED"} />,
                      hint: t("settlement.clearingPart"),
                    },
                  ]
                : []),
              { label: t("settlement.gross"), value: <Directed currency={c} canon={f.gross} dir={f.gross_direction} /> },
              { label: t("settlement.confirmedIn"), value: <Money currency={c} value={f.confirmed_inbound} /> },
              { label: t("settlement.confirmedOut"), value: <Money currency={c} value={f.confirmed_outbound} /> },
              { label: t("settlement.outstanding"), value: <Directed currency={c} canon={f.outstanding} dir={f.direction} /> },
              ...(requestsOpen
                ? [
                    {
                      label: t("settlement.requestsOpen"),
                      value: (
                        <span className="inline-flex flex-wrap items-baseline gap-1">
                          {t("settlement.requestsOpenValue", { count: formatNumber(requestsOpen.count) })}
                          <Money currency={c} value={requestsOpen.outstanding_amount} />
                        </span>
                      ),
                    },
                  ]
                : []),
              ...(receivable
                ? [
                    {
                      label: t("settlement.openReceivable"),
                      value: (
                        <span className="inline-flex flex-wrap items-baseline gap-2">
                          <Money currency={c} value={receivable.amount} />
                          <Badge tone="warn">{labelOf(t, "settlement.receivableState", receivable.state)}</Badge>
                        </span>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        </SectionCard>
      </div>

      <SectionCard title={t("settlement.fees")}>
        <KeyValues
          rows={[
            { label: t("settlement.tradedValue"), value: <Money currency={c} value={p.fees.traded_value} /> },
            { label: t("settlement.exchangeFee"), value: <Money currency={c} value={p.fees.exchange_fee} /> },
            { label: t("settlement.brokerCommission"), value: <Money currency={c} value={p.fees.broker_commission} /> },
          ]}
        />
      </SectionCard>

      {scope === "subtree" && (p.branches ?? []).length > 0 && <Branches p={p} />}

      <PaymentRequests key={`${p.broker_id}:${scope}`} brokerId={p.broker_id} scope={scope} />

      <Notes brokerId={p.broker_id} currency={c} scope={scope} caption={p.note_caption} />
      <p className="text-2xs text-t-3">
        <Time ms={p.from_unix_ms} /> – <Time ms={p.to_unix_ms} />
      </p>
    </div>
  );
}

function Directed({ currency, canon, dir }: { currency: string; canon: string; dir: SettlementPositionDirection }) {
  const { t } = useI18n();
  return (
    <span className="inline-flex flex-wrap items-baseline gap-2">
      <Money currency={currency} value={magnitude(canon)} />
      <span className="text-2xs text-t-3">{labelOf(t, "settlement.dir", dir)}</span>
    </span>
  );
}

function Branches({ p }: { p: SettlementPositionResponse }) {
  const { t, formatNumber } = useI18n();
  const navigate = useNavigate();
  const c = p.currency;
  const row = (id: string, name: string, f: SettlementPositionFigures, open: boolean) => (
    <tr key={id}>
      <td>
        <div className="font-medium text-t-1">{name}</div>
        {open && <Id value={id} />}
      </td>
      <td>
        {f.has_activity ? (
          <Badge tone={dirTone(f.direction)}>{labelOf(t, "settlement.dir", f.direction)}</Badge>
        ) : (
          <span className="text-2xs text-t-3">{t("settlement.noActivity")}</span>
        )}
      </td>
      <td className="num">{f.has_activity ? <Money currency={c} value={magnitude(f.outstanding)} /> : "—"}</td>
      <td className="num">{f.transfer_in_required !== "0" ? <Money currency={c} value={f.transfer_in_required} /> : "—"}</td>
      <td className="num">{f.payment_requests_open ? formatNumber(f.payment_requests_open.count) : "—"}</td>
      <td className="text-right">
        {open && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() =>
              void navigate({
                to: "/settlement",
                search: ((prev: Record<string, unknown>) => ({ ...prev, node: id })) as never,
              })
            }
          >
            {t("settlement.open")}
          </Button>
        )}
      </td>
    </tr>
  );
  return (
    <SectionCard title={t("settlement.branches")}>
      <p className="mb-3 text-xs text-t-3">{t("settlement.branchesHint")}</p>
      <TableFrame label={t("settlement.branches")}>
        <table className="tbl">
          <thead>
            <tr>
              <th>{t("settlement.branches")}</th>
              <th>{t("settlement.direction")}</th>
              <th className="num">{t("settlement.outstanding")}</th>
              <th className="num">{t("settlement.transferIn")}</th>
              <th className="num">{t("settlement.openRequestsColumn")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {row(p.broker_id, t("settlement.thisNode"), p.own, false)}
            {(p.branches ?? []).map((b) => row(b.broker_id, b.display_name || b.broker_id, b.figures, true))}
          </tbody>
        </table>
      </TableFrame>
    </SectionCard>
  );
}

function Notes({ brokerId, currency, scope, caption }: { brokerId: string; currency: string; scope: "node" | "subtree"; caption: string }) {
  const { t } = useI18n();
  const params = { currency, scope, limit: 50, offset: 0 };
  const q = useQuery({ queryKey: keys.settlementNotes(brokerId, params), queryFn: () => fetchSettlementNotes(brokerId, params) });
  return (
    <SectionCard title={t("settlement.notes")} className="border-dashed">
      <p className="mb-3 text-xs italic text-t-3">{serverCaption(t, caption)}</p>
      <QueryBlock query={q} lines={3} empty={(d) => (d.items ?? []).length === 0 ? <p className="text-sm text-t-3">{t("settlement.notesEmpty")}</p> : null}>
        {(d) => (
          <ul className="space-y-3">
            {(d.items ?? []).map((n) => (
              <NoteRow key={n.note_id} n={n} caption={caption} />
            ))}
          </ul>
        )}
      </QueryBlock>
    </SectionCard>
  );
}

function NoteRow({ n, caption }: { n: SettlementNoteItem; caption: string }) {
  const { t } = useI18n();
  const superseded = !!n.superseded_by_note_id;
  const status = superseded ? "SUPERSEDED" : n.status;
  return (
    <li className={superseded ? "border-b border-line-1 pb-3 opacity-60" : "border-b border-line-1 pb-3"}>
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={n.direction === "INBOUND" ? "warn" : "accent"}>{labelOf(t, "settlement.noteDir", n.direction)}</Badge>
        <Badge tone={status === "CONFIRMED" ? "yes" : status === "PENDING" ? "warn" : "neutral"}>
          {labelOf(t, "settlement.noteStatus", status)}
        </Badge>
        <Money currency={n.currency} value={n.amount} className="font-semibold" />
      </div>
      <KeyValues
        className="mt-2"
        rows={[
          { label: t("settlement.valueDate"), value: n.value_date },
          { label: t("settlement.reference"), value: n.external_reference || "—" },
          { label: t("settlement.recordedAt"), value: <Time ms={n.created_at_unix_ms} /> },
        ]}
      />
      {n.payment_request_id && (
        <p className="mt-1 text-xs text-t-2">{t("settlement.noteForRequest", { id: n.payment_request_id })}</p>
      )}
      {n.note && <p className="mt-1 text-xs text-t-2">{n.note}</p>}
      {n.captions.filter((c) => c !== caption).map((c) => (
        <p key={c} className="mt-1 text-2xs italic text-t-3">
          {serverCaption(t, c)}
        </p>
      ))}
    </li>
  );
}

const REQUESTS_PAGE = 25;

/**
 * The payment requests the exchange sent this node — and, with the subtree
 * switch, its branches — newest first. Read only: the exchange records and
 * cancels them; a request is PAID when linked confirmed INBOUND notes
 * cover it. Keyed by node and scope by the caller, so a switch starts at
 * the first page.
 */
function PaymentRequests({ brokerId, scope }: { brokerId: string; scope: "node" | "subtree" }) {
  const { t } = useI18n();
  const [filter, setFilter] = useState<"open" | "all">("open");
  const [offset, setOffset] = useState(0);
  const params = { scope, status: filter === "open" ? "REQUESTED" : undefined, limit: REQUESTS_PAGE, offset };
  const q = useQuery({
    queryKey: keys.paymentRequests(brokerId, params),
    queryFn: () => fetchPaymentRequests(brokerId, params),
    placeholderData: keepPreviousData,
  });
  return (
    <SectionCard
      title={t("settlement.requests")}
      actions={
        <Segmented<"open" | "all">
          label={t("settlement.requestsFilter")}
          value={filter}
          options={[
            { value: "open", label: t("settlement.requestsFilter.open") },
            { value: "all", label: t("settlement.requestsFilter.all") },
          ]}
          onChange={(v) => {
            setFilter(v);
            setOffset(0);
          }}
        />
      }
    >
      <p className="mb-3 text-xs text-t-3">{t("settlement.requestsHint")}</p>
      <QueryBlock
        query={q}
        lines={3}
        empty={(d) =>
          (d.items ?? []).length === 0 && !d.page?.total ? (
            <p className="text-sm text-t-3">{filter === "open" ? t("settlement.requestsEmptyOpen") : t("settlement.requestsEmpty")}</p>
          ) : null
        }
      >
        {(d) => {
          const items = d.items ?? [];
          const captions = splitRequestCaptions(items);
          if (items.length === 0) {
            // a page past the end (requests paid or cancelled since it was opened) keeps the pager to step back (SM-957)
            return (
              <>
                <p className="text-sm text-t-3">{filter === "open" ? t("settlement.requestsEmptyOpen") : t("settlement.requestsEmpty")}</p>
                <Pager page={d.page} onOffset={setOffset} />
              </>
            );
          }
          return (
            <>
              {captions.list.map((c) => (
                <p key={c} className="mb-3 text-xs italic text-t-3">
                  {serverCaption(t, c)}
                </p>
              ))}
              <TableFrame label={t("settlement.requests")}>
                <table className="tbl">
                  <thead>
                    <tr>
                      <th scope="col">{t("settlement.requestNode")}</th>
                      <th scope="col" className="num">
                        {t("settlement.requested")}
                      </th>
                      <th scope="col" className="num">
                        {t("settlement.received")}
                      </th>
                      <th scope="col" className="num">
                        {t("settlement.requestOutstanding")}
                      </th>
                      <th scope="col">{t("settlement.dueDate")}</th>
                      <th scope="col">{t("settlement.reference")}</th>
                      <th scope="col">{t("settlement.requestNote")}</th>
                      <th scope="col">{t("common.status")}</th>
                      <th scope="col">{t("settlement.requestedAt")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((r) => (
                      <RequestRow key={r.request_id} r={r} self={r.broker_id === brokerId} otherCaptions={captions.other(r.captions)} />
                    ))}
                  </tbody>
                </table>
              </TableFrame>
              <Pager page={d.page} onOffset={setOffset} />
            </>
          );
        }}
      </QueryBlock>
    </SectionCard>
  );
}

function RequestRow({ r, self, otherCaptions }: { r: PaymentRequestItem; self: boolean; otherCaptions: string[] }) {
  const { t } = useI18n();
  const label = paymentRequestStatusLabel(r.status);
  const outstanding = requestOutstanding(r);
  return (
    <tr>
      <td>
        <div className="font-medium text-t-1">{self ? t("settlement.requestThisNode") : r.broker_display_name || r.broker_id}</div>
        <Id value={r.request_id} />
      </td>
      <td className="num">
        <Money currency={r.currency} value={r.amount} />
      </td>
      <td className="num">
        <Money currency={r.currency} value={r.received_amount} />
      </td>
      <td className="num">{outstanding === null ? "—" : <Money currency={r.currency} value={outstanding} />}</td>
      <td className="text-xs">
        <span className="whitespace-nowrap">{r.due_date || "—"}</span>
        {isOverdue(r.due_date, r.status) && (
          <span className="mt-1 block">
            <Badge tone="no">{t("settlement.overdue")}</Badge>
          </span>
        )}
      </td>
      <td className="max-w-48 break-words text-xs">{r.reference || "—"}</td>
      <td className="max-w-72 text-xs text-t-2">
        {r.note || (otherCaptions.length === 0 ? "—" : null)}
        {otherCaptions.map((c) => (
          <span key={c} className="mt-1 block text-2xs italic text-t-3">
            {serverCaption(t, c)}
          </span>
        ))}
      </td>
      <td className="text-xs">
        <Badge tone={paymentRequestStatusTone(r.status)}>{label ? t(label) : r.status}</Badge>
        {r.status === "PAID" && r.paid_at_unix_ms ? (
          <span className="mt-1 block text-2xs text-t-3">
            {t("settlement.paidOn")} <Time ms={r.paid_at_unix_ms} />
          </span>
        ) : null}
        {r.status === "CANCELLED" && r.cancel_reason && (
          <span className="mt-1 block max-w-60 text-2xs text-t-2">{t("settlement.cancelReason", { reason: r.cancel_reason })}</span>
        )}
      </td>
      <td className="text-xs">
        <Time ms={r.created_at_unix_ms} />
      </td>
    </tr>
  );
}
